import { motion } from "framer-motion"
import { BookOpen, TrendingUp, Home, Check } from "lucide-react"
import { springPresets } from "@/lib/motion"

interface PhoneMockupProps {
  className?: string
}

export default function PhoneMockup({ className = "" }: PhoneMockupProps) {
  return (
    <div className={`relative ${className}`}>
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-accent/30 via-primary/20 to-accent/30 blur-3xl"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 0.7, 0.5],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="relative z-10"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springPresets.gentle}
      >
        <motion.div
          animate={{
            y: [0, -12, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="w-[320px] mx-auto"
        >
          <div className="relative bg-[#1a1a1a] rounded-[48px] p-3 shadow-2xl">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-[#1a1a1a] rounded-b-3xl z-20" />

            <div className="relative bg-[#0a0a0a] rounded-[36px] overflow-hidden h-[640px]">
              <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />

              <div className="relative h-full flex flex-col">
                <div className="bg-gradient-to-r from-accent to-primary px-6 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-accent to-primary" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">Sammo</div>
                    <div className="text-white/80 text-xs">Online</div>
                  </div>
                </div>

                <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, ...springPresets.gentle }}
                    className="flex gap-2"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-primary flex-shrink-0" />
                    <div className="bg-[#1f2937] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[240px]">
                      <p className="text-white text-sm leading-relaxed">
                        Welcome to Sammo! Practical support for your home, health, money and everyday life is now just a message away.
                      </p>
                      <div className="text-white/50 text-xs mt-1">10:32</div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, ...springPresets.gentle }}
                    className="space-y-2"
                  >
                    <div className="flex gap-2">
                      <div className="w-8 h-8 flex-shrink-0" />
                      <div className="space-y-2 flex-1">
                        <button className="w-full bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30 rounded-xl px-4 py-3 flex items-center gap-3 hover:from-accent/30 hover:to-primary/30 transition-all">
                          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                            <BookOpen className="w-4 h-4 text-accent" />
                          </div>
                          <span className="text-white text-sm font-medium">Learn & Improve</span>
                        </button>

                        <button className="w-full bg-gradient-to-r from-primary/30 to-accent/30 border border-primary/50 rounded-xl px-4 py-3 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/30 flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-accent" />
                          </div>
                          <span className="text-white text-sm font-medium">Money & Business</span>
                          <Check className="w-4 h-4 text-accent ml-auto" />
                        </button>

                        <button className="w-full bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30 rounded-xl px-4 py-3 flex items-center gap-3 hover:from-accent/30 hover:to-primary/30 transition-all">
                          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                            <Home className="w-4 h-4 text-accent" />
                          </div>
                          <span className="text-white text-sm font-medium">Home & Support</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9, ...springPresets.gentle }}
                    className="flex gap-2 justify-end"
                  >
                    <div className="bg-accent/90 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[200px]">
                      <p className="text-white text-sm">Money & Business</p>
                      <div className="text-white/70 text-xs mt-1">10:33</div>
                    </div>
                    <div className="w-8 h-8 flex-shrink-0" />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.2, ...springPresets.gentle }}
                    className="flex gap-2"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-primary flex-shrink-0" />
                    <div className="bg-[#1f2937] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[240px]">
                      <p className="text-white text-sm leading-relaxed">
                        Great choice! Here's a quick tip: Track your spending for 7 days to see where your money goes. Small daily expenses add up fast!
                      </p>
                      <div className="text-white/50 text-xs mt-1">10:33</div>
                    </div>
                  </motion.div>
                </div>

                <div className="p-4 bg-[#1a1a1a]/50 backdrop-blur-sm border-t border-white/10">
                  <div className="bg-[#2a2a2a] rounded-full px-4 py-3 flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      className="flex-1 bg-transparent text-white text-sm placeholder:text-white/40 outline-none"
                      disabled
                    />
                    <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}